import React from "react";
import { MdClose, MdLogin, MdLogout, MdOutlineMap } from "react-icons/md";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";
import { AttendanceResponse } from "services/userPortalService";
import { reverseGeocodeVietmapCoordinates } from "services/vietmapService";
import { buildGoogleMapsUrl, parseLatLngString } from "utils/geo";

interface LocationBlockProps {
  label: string;
  rawLocation: string | null | undefined;
}

/**
 * Resolves a raw "lat,lng" location string to a human-readable address via
 * the Vietmap reverse-geocode proxy. The Google Maps link is always shown
 * (built directly from the raw coordinates) regardless of whether
 * reverse-geocoding succeeds, so the student always has a way to see the
 * location even if the address lookup fails.
 */
function LocationBlock({ label, rawLocation }: LocationBlockProps) {
  const { t } = useLanguage();
  const [resolvedAddress, setResolvedAddress] = React.useState<string | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);

  const coords = React.useMemo(
    () => parseLatLngString(rawLocation),
    [rawLocation]
  );

  React.useEffect(() => {
    if (!coords) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setResolvedAddress(null);
    reverseGeocodeVietmapCoordinates(coords.lat, coords.lng, controller.signal)
      .then((res) => {
        if (!active) return;
        const label2 = res.display || res.address || res.name || null;
        setResolvedAddress(label2);
      })
      .catch(() => {
        // Reverse-geocoding failure is non-fatal — fall back to raw coords.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [coords]);

  if (!rawLocation) {
    return (
      <div className="rounded-xl border border-line p-3">
        <p className="text-xs font-semibold uppercase text-ink-muted">
          {label}
        </p>
        <p className="mt-1 text-sm text-ink-subtle">
          {t("attendanceHistory.noLocation")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line p-3">
      <p className="text-xs font-semibold uppercase text-ink-muted">
        {label}
      </p>
      <p className="mt-1 text-sm text-ink">
        {loading
          ? t("attendanceHistory.resolvingAddress")
          : resolvedAddress || rawLocation}
      </p>
      {coords && (
        <a
          href={buildGoogleMapsUrl(coords.lat, coords.lng)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-brand-500 hover:underline"
        >
          <MdOutlineMap className="h-4 w-4" />
          {t("attendanceHistory.viewOnGoogleMaps")}
        </a>
      )}
    </div>
  );
}

interface PhotoBlockProps {
  label: string;
  url: string | null | undefined;
}

function PhotoBlock({ label, url }: PhotoBlockProps) {
  const { t } = useLanguage();
  return (
    <div className="flex-1">
      <p className="mb-1 text-xs font-semibold uppercase text-ink-muted">
        {label}
      </p>
      {url ? (
        <img
          src={url}
          alt={label}
          className="max-h-72 w-full rounded-lg bg-gray-50 object-contain dark:bg-canvas/40"
        />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-dashed border-line text-xs text-ink-subtle">
          {t("attendanceHistory.noPhoto")}
        </div>
      )}
    </div>
  );
}

interface TimeBlockProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function TimeBlock({ label, value, icon }: TimeBlockProps) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-line p-3">
      <div className="mt-0.5 text-ink-subtle">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-ink-muted">
          {label}
        </p>
        <p className="mt-0.5 truncate text-sm font-semibold text-ink">
          {value}
        </p>
      </div>
    </div>
  );
}

export default function AttendanceDetailModal({
  record,
  onClose,
}: {
  record: AttendanceResponse;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  // Slide the drawer in from the left after mount instead of just fading
  // the overlay in, so the panel's origin edge is legible as "left side".
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) return t("attendanceHistory.notAvailable");
    return new Date(value).toLocaleString("vi-VN");
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-start bg-black/40 transition-opacity duration-300 ${
        mounted ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={`flex h-full w-full max-w-md transform flex-col bg-surface shadow-2xl transition-transform duration-300 ${ mounted ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-line p-5">
          <h3 className="text-lg font-bold text-ink">
            {t("attendanceHistory.detailTitle")}
          </h3>
          <ActionButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="!p-1 text-ink-muted"
          >
            <MdClose className="h-5 w-5" />
          </ActionButton>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="flex flex-col gap-4">
            {(record.class_name ||
              record.session_title ||
              record.session_number) && (
              <div className="rounded-xl border border-line bg-gray-50 p-4 dark:bg-canvas/40">
                {record.class_name && (
                  <p className="text-base font-bold text-ink">
                    {record.class_name}
                  </p>
                )}
                {record.venue_name && (
                  <p className="text-xs text-ink-subtle">
                    {record.venue_name}
                  </p>
                )}
                <p className="mt-0.5 text-sm text-ink-muted">
                  {record.session_number
                    ? `${t("attendanceHistory.session")} ${record.session_number}`
                    : null}
                  {record.session_number && record.session_title ? " · " : ""}
                  {record.session_title || ""}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TimeBlock
                label={t("attendanceHistory.checkedInAt")}
                value={formatDateTime(record.checked_in_at)}
                icon={<MdLogin className="h-5 w-5" />}
              />
              <TimeBlock
                label={t("attendanceHistory.checkedOutAt")}
                value={formatDateTime(record.checked_out_at)}
                icon={<MdLogout className="h-5 w-5" />}
              />
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("attendanceHistory.photos")}
              </p>
              <div className="flex flex-col gap-3">
                <PhotoBlock
                  label={t("attendanceHistory.checkInPhoto")}
                  url={record.check_in_snapshot_url}
                />
                {record.check_out_snapshot_url && (
                  <PhotoBlock
                    label={t("attendanceHistory.checkOutPhoto")}
                    url={record.check_out_snapshot_url}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {t("attendanceHistory.locations")}
              </p>
              <LocationBlock
                label={t("attendanceHistory.checkInLocation")}
                rawLocation={record.check_in_location}
              />
              {record.checkout_location &&
                record.checkout_location !== record.check_in_location && (
                  <LocationBlock
                    label={t("attendanceHistory.checkOutLocation")}
                    rawLocation={record.checkout_location}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
